import { type CityRef, referenceApi } from '@/shared/api';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { useTranslate } from '@tolgee/react';
import { useEffect, useState } from 'react';

export interface ZipCityValue {
  zipCode: string;
  /** geolocalisation id of the chosen town */
  geoId?: number;
  cityLabel: string;
}

interface ZipCityPickerProps {
  value: ZipCityValue;
  onChange: (value: Partial<ZipCityValue>) => void;
  required?: boolean;
  /** prefix for element ids (several pickers on a page) */
  idPrefix?: string;
}

/**
 * Legacy step4 / profile edit: postal code (FR 5 digits, BE 4 digits) then the
 * town picked among the ones sharing that code (a zip may cover several towns).
 * The country shown on the profile derives from the chosen town.
 */
export function ZipCityPicker({ value, onChange, required, idPrefix = '' }: ZipCityPickerProps) {
  const { t } = useTranslate();
  const [cities, setCities] = useState<CityRef[]>([]);
  const [lookedUpZip, setLookedUpZip] = useState('');
  const zipId = `${idPrefix}zipcode`;
  const cityId = `${idPrefix}city`;

  // biome-ignore lint/correctness/useExhaustiveDependencies: onChange is stable from parent
  useEffect(() => {
    const zip = value.zipCode.trim();
    if (!/^\d{4,5}$/.test(zip)) {
      setCities([]);
      setLookedUpZip('');
      return;
    }
    if (zip === lookedUpZip) return;
    let cancelled = false;
    referenceApi
      .findCitiesByZip(zip)
      .then((rows) => {
        if (cancelled) return;
        setCities(rows);
        setLookedUpZip(zip);
        const current = rows.find((c) => c.geoId === value.geoId);
        if (current) {
          onChange({ cityLabel: current.city });
        } else if (rows.length === 1) {
          onChange({ geoId: rows[0].geoId, cityLabel: rows[0].city });
        } else {
          // several towns and none is the member's: they must pick one
          onChange({ geoId: undefined, cityLabel: '' });
        }
      })
      .catch(() => {
        if (!cancelled) setCities([]);
      });
    return () => {
      cancelled = true;
    };
  }, [value.zipCode]);

  const mark = required ? <span className="text-destructive"> *</span> : null;

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor={zipId}>
            {t('signup.fields.zipcode')}
            {mark}
          </Label>
          <Input
            id={zipId}
            type="text"
            inputMode="numeric"
            placeholder="75011"
            value={value.zipCode}
            onChange={(e) => onChange({ zipCode: e.target.value.replace(/\D/g, '').slice(0, 5) })}
            maxLength={5}
            required={required}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor={cityId}>
            {t('signup.fields.city')}
            {mark}
          </Label>
          <Select
            value={value.geoId ? String(value.geoId) : undefined}
            onValueChange={(v) => {
              const city = cities.find((c) => c.geoId === Number(v));
              onChange({ geoId: Number(v), cityLabel: city?.city ?? '' });
            }}
            disabled={cities.length === 0}
          >
            <SelectTrigger id={cityId} data-testid={`select-${cityId}`}>
              <SelectValue placeholder={value.cityLabel || t('signup.placeholders.select')} />
            </SelectTrigger>
            <SelectContent>
              {cities.map((city) => (
                <SelectItem key={city.geoId} value={String(city.geoId)}>
                  {city.city} ({city.country})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <p className="text-xs text-muted-foreground">
        {lookedUpZip && cities.length === 0 ? t('signup.hints.noCity') : t('signup.hints.zip')}
      </p>
    </div>
  );
}
